import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { analysisService } from '../../services/analysisService';
import { corpusService } from '../../services/corpusService';
import toast from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import WebList from '../../components/analysis/WebList';
import { AnalysisProgressBar } from '../../components/analysis/AnalysisProgressBar';
import { generateUUID } from '../../utils/uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faBalanceScale,
  faCoins,
  faBuilding,
  faGraduationCap,
  faIndustry,
  faGlobe,
  faLaptopCode,
  faHospital,
  faLink
} from '@fortawesome/free-solid-svg-icons';
import { CorpusSector } from '../../types';

// Map sector codes to Font Awesome icons
const getSectorIcon = (sector: CorpusSector) => {
  const iconMap: Record<string, any> = {
    'GENERAL': faFileAlt,
    'LEGAL': faBalanceScale,
    'FINANCE': faCoins,
    'finanzas': faCoins,
    'ADMIN': faBuilding,
    'HEALTH': faHospital,
    'EDUCATION': faGraduationCap,
    'INDUSTRY': faIndustry,
    'TECH': faLaptopCode,
  };

  return iconMap[sector.code] || faGlobe;
};

export default function WebAnalysisPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('new');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    url: '',
    sectorIds: [] as number[],
    title: '',
  });

  const { data: sectorsData } = useQuery({
    queryKey: ['sectors'],
    queryFn: corpusService.getSectors,
  });

  // Find General sector ID and initialize it
  const generalSector = sectorsData?.sectors.find(s => s.code === 'GENERAL');
  const generalSectorId = generalSector?.id;

  // Auto-select General sector when it's loaded
  useEffect(() => {
    if (generalSectorId && !formData.sectorIds.includes(generalSectorId)) {
      setFormData(prev => ({
        ...prev,
        sectorIds: [generalSectorId]
      }));
    }
  }, [generalSectorId]);

  const analyzeMutation = useMutation({
    mutationFn: analysisService.analyzeUrl,
    onSuccess: (_data) => {
      // Progress bar will handle navigation on completion
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al analizar la URL');
      setCurrentSessionId(null);
    },
  });

  const handleAnalysisComplete = (analysisId: number) => {
    toast.success('Página web analizada correctamente');
    setCurrentSessionId(null);
    setFormData({ url: '', sectorIds: generalSectorId ? [generalSectorId] : [], title: '' });
    // Navigate directly to sections list instead of global analysis
    navigate(`/analysis/web/${analysisId}/sections`);
  };

  const toggleSector = (sectorId: number) => {
    // Prevent deselecting General sector
    if (sectorId === generalSectorId) {
      return;
    }

    setFormData(prev => {
      const isSelected = prev.sectorIds.includes(sectorId);
      return {
        ...prev,
        sectorIds: isSelected
          ? prev.sectorIds.filter(id => id !== sectorId)
          : [...prev.sectorIds, sectorId]
      };
    });
  };

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.url.trim()) {
      toast.error('Debes ingresar una URL');
      return;
    }

    if (!validateUrl(formData.url)) {
      toast.error('La URL ingresada no es válida');
      return;
    }

    if (formData.sectorIds.length < 2) {
      toast.error('Debes seleccionar al menos un sector adicional además de General');
      return;
    }

    // Generate session ID for progress tracking
    const sessionId = generateUUID();
    setCurrentSessionId(sessionId);

    const submitData = {
      url: formData.url,
      sectorIds: formData.sectorIds,
      sessionId,
      title: formData.title,
    };

    analyzeMutation.mutate(submitData);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-green-600 rounded-xl p-8 shadow-lg">
        <h1 className="text-3xl font-bold mb-2 text-white">Análisis Web</h1>
        <p className="text-white/90">
          Ingresa la URL de una página web para analizar su contenido
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="new">Nuevo Análisis</TabsTrigger>
          <TabsTrigger value="history">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-6">
          {/* Progress Bar */}
          {currentSessionId && (
            <AnalysisProgressBar
              sessionId={currentSessionId}
              onComplete={handleAnalysisComplete}
            />
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sector Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Seleccionar Sectores *</CardTitle>
                <CardDescription>
                  Elige uno o varios sectores para analizar la página web con vocabulario especializado.
                  {formData.sectorIds.length >= 2 ? (
                    <span className="block mt-1 text-primary font-medium">
                      {formData.sectorIds.length} sectores seleccionados (General + {formData.sectorIds.length - 1} adicional{formData.sectorIds.length > 2 ? 'es' : ''})
                    </span>
                  ) : (
                    <span className="block mt-1 text-destructive font-medium">
                      Debes seleccionar al menos un sector adicional
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {sectorsData?.sectors
                    .slice()
                    .sort((a, b) => {
                      // General always first
                      if (a.code === 'GENERAL') return -1;
                      if (b.code === 'GENERAL') return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map((sector) => {
                      const isSelected = formData.sectorIds.includes(sector.id);
                      const isGeneral = sector.code === 'GENERAL';
                      return (
                        <button
                          key={sector.id}
                          type="button"
                          onClick={() => toggleSector(sector.id)}
                          disabled={isGeneral}
                          className={`p-5 rounded-lg border-2 transition-all hover:shadow-md relative ${
                            isSelected
                              ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20'
                              : 'border-border hover:border-primary/50 bg-card'
                          } ${isGeneral ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'}`}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                              ✓
                            </div>
                          )}
                          {isGeneral && (
                            <div className="absolute top-2 left-2 bg-blue-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                              OBLIGATORIO
                            </div>
                          )}
                          <div className={`text-3xl mb-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                            <FontAwesomeIcon icon={getSectorIcon(sector)} />
                          </div>
                          <div className={`font-semibold text-sm ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                            {sector.name}
                          </div>
                        </button>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* URL Input */}
            <Card>
              <CardHeader>
                <CardTitle>URL a analizar *</CardTitle>
                <CardDescription>
                  Ingresa la dirección completa de la página web (debe comenzar con http:// o https://)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="url"
                      className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                      placeholder="https://ejemplo.com"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faLink} className="text-muted-foreground text-2xl" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  La herramienta extraerá y analizará el contenido de texto de la página web.
                </p>
              </CardContent>
            </Card>

            {/* Title */}
            <Card>
              <CardContent className="pt-6">
                <label className="block text-sm font-medium mb-2">Título del análisis (opcional)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                  placeholder="Ej: Página principal del gobierno"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                type="submit"
                className="flex-1"
                disabled={analyzeMutation.isPending || !formData.url.trim() || formData.sectorIds.length < 2}
              >
                {analyzeMutation.isPending ? 'Analizando página...' : 'Analizar Página Web'}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <WebList />
        </TabsContent>
      </Tabs>
    </div>
  );
}
